/*
 * Copyright (c) 2025.
 * This code and its derivatives belong to BETICA LTD and may not be copied, reproduced, amended or used in any way without the permission of BETICA LTD
 * @author torrejonv
 *
 */

/**
 * @file recommenderSlice.js
 * @description Redux slice for managing recommender related state and operations
 */

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import createAxiosInstance from "../async/axios";
import { getRequestParams } from "../async/get-fetch-params";
import { getAuthAccountId } from "../reselect/auth-selector";

/**
 * @function getInitialState
 * @description Creates the initial state object for the recommender slice
 * @param {Object} params - Initial state parameters
 * @returns {Object} The initial state object
 */
export const getInitialState = () => ({
  errorLeague: null,
  errorMatch: null,
  leagueRecommendations: undefined,
  loadingLeagueRecommendations: false,
  loadingMatchRecommendations: false,
  matchRecommendations: undefined,
});

/**
 * @function loadMatchRecommendations
 * @description Async thunk for load match recommendations operations
 * @param {Object} data - Parameters for the operation
 * @returns {Promise<Object>} Result of the operation
 */
export const loadMatchRecommendations = createAsyncThunk(
  "recommender/loadMatchRecommendations",
  async (data, thunkAPI) => {
    const { authToken, language, lineId, originId } = getRequestParams(thunkAPI.getState());
    const accountId = getAuthAccountId(thunkAPI.getState());
    try {
      const result = await createAxiosInstance(thunkAPI.dispatch, { authToken, language }).get(
        `/player/acc/${accountId}/matchrecommendations?originId=${originId}&lineId=${lineId}`,
      );

      return { matchRecommendations: result.data };
    } catch (err) {
      const customError = {
        message: err.response?.headers["x-information"] || "Unable to load match recommendations", // serializable (err.response.data)
        name: "Load Match Recommendation Error",
        status: err.response?.statusText,
      };
      throw customError;
    }
  },
);

/**
 * @function loadLeagueRecommendations
 * @description Async thunk for load league recommendations operations
 * @param {Object} data - Parameters for the operation
 * @returns {Promise<Object>} Result of the operation
 */
export const loadLeagueRecommendations = createAsyncThunk(
  "recommender/loadLeagueRecommendations",
  async (data, thunkAPI) => {
    const { authToken, language, lineId, originId } = getRequestParams(thunkAPI.getState());
    const accountId = getAuthAccountId(thunkAPI.getState());
    try {
      const result = await createAxiosInstance(thunkAPI.dispatch, { authToken, language }).get(
        `/player/acc/${accountId}/leaguerecommendations?originId=${originId}&lineId=${lineId}`,
      );

      return { leagueRecommendations: result.data };
    } catch (err) {
      const customError = {
        message: err.response?.headers["x-information"] || "Unable to load league recommendation", // serializable (err.response.data)
        name: "Load League Recommendation Error",
        status: err.response?.statusText,
      };
      throw customError;
    }
  },
);

/**
 * @constant recommenderSlice
 * @description Redux slice for recommender management
 */
const recommenderSlice = createSlice({
  extraReducers: (builder) => {
    builder
      .addCase(loadMatchRecommendations.pending, (state) => {
        state.loadingMatchRecommendations = true;
        state.errorMatch = null;
      })
      .addCase(loadMatchRecommendations.rejected, (state, action) => {
        state.loadingMatchRecommendations = false;
        state.errorMatch = action.error.message;
      })
      .addCase(loadMatchRecommendations.fulfilled, (state, action) => {
        state.matchRecommendations = action.payload.matchRecommendations;
        state.errorMatch = null;
        state.loadingMatchRecommendations = false;
      })
      .addCase(loadLeagueRecommendations.pending, (state) => {
        state.loadingLeagueRecommendations = true;
        state.errorLeague = null;
      })
      .addCase(loadLeagueRecommendations.rejected, (state, action) => {
        state.loadingLeagueRecommendations = false;
        state.errorLeague = action.error.message;
      })
      .addCase(loadLeagueRecommendations.fulfilled, (state, action) => {
        state.leagueRecommendations = action.payload.leagueRecommendations;
        state.errorLeague = null;
        state.loadingLeagueRecommendations = false;
      });
  },
  initialState: getInitialState(),
  name: "recommender",
  // reducers actions
  reducers: {
    //
  },
});
const { reducer } = recommenderSlice;
export default reducer;
